import axios from 'axios';
import authService from './authService';
import { getCurrentConfig } from '../config/environmentConfig';

class FlyShieldApiService {
  constructor() {
    this.endpoint = '/flyshield-detector-request';
  }

  get baseUrl() {
    return `${getCurrentConfig().apiBaseUrl}/api/indirectmonitoring`;
  }

  async submitDetectorRequest(requestData) {
    try {
      // Get the access token
      const accessToken = await authService.getAccessToken();

      // Validate required fields
      this.validateRequest(requestData);

      // Make the API request
      const response = await axios.post(
        `${this.baseUrl}${this.endpoint}`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          timeout: 60000, // 60 seconds timeout for large image uploads
        }
      );

      return {
        success: true,
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      console.error('API request failed:', error);

      let errorMessage = 'Unknown error occurred';
      let statusCode = null;
      let responseData = null;

      if (error.response) {
        // Server responded with error status
        statusCode = error.response.status;
        responseData = error.response.data;

        if (statusCode === 401) {
          // Clear token and retry once
          authService.clearToken();
          errorMessage = 'Authentication failed. Please try again.';
        } else if (statusCode === 400) {
          errorMessage = `Bad Request: ${responseData?.message || 'Invalid request data'}`;
        } else if (statusCode === 403) {
          errorMessage = 'Access forbidden. Check your permissions.';
        } else if (statusCode === 404) {
          errorMessage = 'API endpoint not found.';
        } else if (statusCode >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `HTTP ${statusCode}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Request made but no response received
        errorMessage = 'No response from server. Check your network connection.';
      } else {
        // Something else happened
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        statusCode,
        responseData,
        originalError: error,
      };
    }
  }

  validateRequest(requestData) {
    const requiredFields = [
      'photo01',
      'photo02',
      'photo03',
      'companyId',
      'companyUnitId',
      'companyFarmId',
      'trickSectionFieldId',
      'flyTrapId',
      'latitude',
      'longitude',
      'userEmail',
      'collectDateTime',
      'userId'
    ];

    const missingFields = requiredFields.filter(field =>
      !requestData[field] || requestData[field] === ''
    );

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestData.userEmail)) {
      throw new Error('Invalid email format');
    }

    // Validate numeric fields
    const numericFields = ['companyId', 'companyUnitId', 'companyFarmId', 'trickSectionFieldId', 'flyTrapId'];
    numericFields.forEach(field => {
      if (isNaN(requestData[field]) || requestData[field] <= 0) {
        throw new Error(`${field} must be a positive number`);
      }
    });

    // Validate coordinates
    const lat = parseFloat(requestData.latitude);
    const lng = parseFloat(requestData.longitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      throw new Error('Latitude must be a number between -90 and 90');
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      throw new Error('Longitude must be a number between -180 and 180');
    }

    // Validate datetime format
    try {
      new Date(requestData.collectDateTime).toISOString();
    } catch (error) {
      throw new Error('Invalid date format for collectDateTime');
    }

    // Validate base64 images
    const imageFields = ['photo01', 'photo02', 'photo03'];
    imageFields.forEach(field => {
      const base64 = requestData[field];
      if (!this.isValidBase64(base64)) {
        throw new Error(`${field} is not a valid base64 image`);
      }
    });
  }

  isValidBase64(str) {
    try {
      // Check if it's a valid base64 string
      const decoded = atob(str);
      const encoded = btoa(decoded);
      return encoded === str && str.length > 0;
    } catch (error) {
      return false;
    }
  }

  // Helper method to format request for display
  formatRequestForDisplay(requestData) {
    const displayData = { ...requestData };

    // Truncate base64 images for display
    ['photo01', 'photo02', 'photo03'].forEach(field => {
      if (displayData[field]) {
        displayData[field] = `${displayData[field].substring(0, 50)}... (${displayData[field].length} characters)`;
      }
    });

    return displayData;
  }
}

export default new FlyShieldApiService();
